// src/pages/Dashboard/Admin/Messages/utils/messagesDashboardUtils.js

export const MESSAGE_SUBJECTS = [
  "Adhésion",
  "Dossier d'inscription",
  "Règlement",
  "Informations cours",
  "Absence",
  "Inscription compétition",
  "Perte d'objet",
  "Autre",
];

export const DEFAULT_MESSAGES_FILTERS = {
  source: "all", // all | chat | private
  query: "",
  group: "",
  subject: "",
  showTeamMessages: false,
};

export function safeArray(value) {
  return Array.isArray(value) ? value : [];
}

export function normalizeText(value = "") {
  return String(value)
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();
}

export function stripHtml(html) {
  if (!html) return "";

  return String(html)
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&")
    .replace(/&quot;/g, '"')
    .replace(/&#039;/g, "'")
    .replace(/\s+/g, " ")
    .trim();
}

export function truncateText(value, max = 220) {
  const text = String(value || "").trim();

  if (text.length <= max) return text;

  return `${text.slice(0, max).trim()}…`;
}

export function formatDateTimeFR(value) {
  if (!value) return "—";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) return "—";

  return new Intl.DateTimeFormat("fr-FR", {
    day: "2-digit",
    month: "2-digit",
    year: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  })
    .format(date)
    .replace(",", " -");
}

export function formatDateShort(value) {
  if (!value) return "—";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) return "—";

  return new Intl.DateTimeFormat("fr-FR", {
    day: "2-digit",
    month: "2-digit",
    year: "2-digit",
  }).format(date);
}

export function getFullName(person) {
  return `${person?.prenom || ""} ${person?.nom || ""}`.trim();
}

export function getInitials(name = "") {
  const parts = String(name).trim().split(/\s+/).filter(Boolean);

  if (!parts.length) return "U";

  return parts
    .slice(0, 2)
    .map((part) => part[0])
    .join("")
    .toUpperCase();
}

export function getChatGroupColor(groupName, groupColors = {}) {
  const color = groupColors[groupName];

  if (!color) return null;

  if (String(color).startsWith("#") || String(color).startsWith("rgb")) {
    return color;
  }

  return null;
}

export function getSubjectBadgeClass(subject) {
  switch (subject) {
    case "Adhésion":
      return "bg-emerald-50 text-emerald-700 ring-emerald-200 dark:bg-emerald-500/10 dark:text-emerald-300 dark:ring-emerald-500/20";

    case "Dossier d'inscription":
      return "bg-sky-50 text-sky-700 ring-sky-200 dark:bg-sky-500/10 dark:text-sky-300 dark:ring-sky-500/20";

    case "Règlement":
      return "bg-amber-50 text-amber-700 ring-amber-200 dark:bg-amber-500/10 dark:text-amber-300 dark:ring-amber-500/20";

    case "Informations cours":
      return "bg-indigo-50 text-indigo-700 ring-indigo-200 dark:bg-indigo-500/10 dark:text-indigo-300 dark:ring-indigo-500/20";

    case "Absence":
      return "bg-rose-50 text-rose-700 ring-rose-200 dark:bg-rose-500/10 dark:text-rose-300 dark:ring-rose-500/20";

    case "Inscription compétition":
      return "bg-orange-50 text-orange-700 ring-orange-200 dark:bg-orange-500/10 dark:text-orange-300 dark:ring-orange-500/20";

    case "Perte d'objet":
      return "bg-fuchsia-50 text-fuchsia-700 ring-fuchsia-200 dark:bg-fuchsia-500/10 dark:text-fuchsia-300 dark:ring-fuchsia-500/20";

    case "Autre":
    default:
      return "bg-slate-100 text-slate-700 ring-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:ring-slate-700";
  }
}

export function getSourceMeta(source) {
  if (source === "chat") {
    return {
      label: "Chat",
      badge:
        "bg-violet-50 text-violet-700 ring-violet-200 dark:bg-violet-500/10 dark:text-violet-300 dark:ring-violet-500/20",
      row: "border-violet-200/80 dark:border-violet-500/20",
    };
  }

  return {
    label: "Message",
    badge:
      "bg-indigo-50 text-indigo-700 ring-indigo-200 dark:bg-indigo-500/10 dark:text-indigo-300 dark:ring-indigo-500/20",
    row: "border-indigo-200/80 dark:border-indigo-500/20",
  };
}

export function mapChatMessage(message, groupColors = {}) {
  const senderName = getFullName(message?.Users) || "Utilisateur";
  const groupName = message?.group?.nom || "Groupe";
  const content = message?.message || "";
  const createdAt = message?.createdAt || message?.created_at || message?.updatedAt;

  return {
    key: `chat-${message.id}`,
    id: message.id,
    source: "chat",
    sourceLabel: "Chat",
    senderName,
    recipientName: groupName,
    subject: groupName,
    groupName,
    groupColor: getChatGroupColor(groupName, groupColors),
    content,
    contentPreview: truncateText(content, 240),
    date: createdAt,
    raw: message,
    searchable: normalizeText(`${senderName} ${groupName} ${content}`),
  };
}

export function mapPrivateMessage(message) {
  const senderName = getFullName(message?.Sender) || "Expéditeur";
  const recipientName = getFullName(message?.Recipient) || "Destinataire";
  const subject = message?.subject || "Autre";
  const content = stripHtml(message?.content || "");
  const createdAt = message?.created_at || message?.createdAt;

  return {
    key: `private-${message.id}`,
    id: message.id,
    source: "private",
    sourceLabel: "Message",
    senderName,
    recipientName,
    subject,
    groupName: "",
    groupColor: null,
    content,
    contentPreview: truncateText(content, 240),
    date: createdAt,
    senderId: message?.sender_id,
    raw: message,
    searchable: normalizeText(`${senderName} ${recipientName} ${subject} ${content}`),
  };
}

export function sortMessagesByDate(messages) {
  return safeArray(messages)
    .slice()
    .sort((a, b) => {
      const dateA = new Date(a?.date || 0).getTime();
      const dateB = new Date(b?.date || 0).getTime();

      if (dateA !== dateB) return dateB - dateA;

      return String(b?.key || "").localeCompare(String(a?.key || ""), "fr");
    });
}

export function filterUnifiedMessages(messages, filters, adminInstructorIds = []) {
  const query = normalizeText(filters?.query || "");
  const source = filters?.source || "all";
  const group = filters?.group || "";
  const subject = filters?.subject || "";
  const showTeamMessages = !!filters?.showTeamMessages;

  return sortMessagesByDate(messages).filter((message) => {
    const matchSource = source === "all" || message.source === source;

    const matchQuery = !query || message.searchable.includes(query);

    const matchGroup =
      !group || (message.source === "chat" && message.groupName === group);

    const matchSubject =
      !subject ||
      (message.source === "private" && message.subject === subject);

    const matchTeam =
      showTeamMessages ||
      message.source !== "private" ||
      !adminInstructorIds.includes(message.senderId);

    return matchSource && matchQuery && matchGroup && matchSubject && matchTeam;
  });
}

export function calculateMessagesStats(messages, adminInstructorIds = []) {
  const rows = safeArray(messages);

  let chat = 0;
  let privateMessages = 0;
  let teamMessages = 0;

  const groups = new Set();
  const subjects = new Set();

  for (const message of rows) {
    if (message.source === "chat") {
      chat += 1;
      if (message.groupName) groups.add(message.groupName);
    }

    if (message.source === "private") {
      privateMessages += 1;
      if (message.subject) subjects.add(message.subject);
      if (adminInstructorIds.includes(message.senderId)) teamMessages += 1;
    }
  }

  return {
    total: rows.length,
    chat,
    privateMessages,
    teamMessages,
    groupsCount: groups.size,
    subjectsCount: subjects.size,
  };
}